import cn from 'classnames';
import React, { useEffect, useState } from 'react';
import { NavLink, Route, RouteComponentProps } from 'react-router-dom';

interface NavProps {
  children: React.ReactNode;
  collapsible?: boolean;
  collapsed?: boolean;
  onStateChange?: (collapsed: boolean) => void;
}

type ChildProps = GroupProps | LinkProps;

export const VerticalNav = React.forwardRef<HTMLDivElement, NavProps>(
  (props, ref) => {
    const { children, collapsible, collapsed = true, onStateChange } = props;

    let hasGroup = false;
    let hasIcons = false;

    const isGroup = (child: React.ReactNode) =>
      React.isValidElement<GroupProps>(child) &&
      child.type === VerticalNavGroup;

    const isIcon = (child: React.ReactNode) =>
      React.isValidElement<ChildProps>(child) &&
      Array.isArray(child.props.children) &&
      child.props.children.some(
        grandChild =>
          React.isValidElement<IconProps>(grandChild) &&
          grandChild.type === VerticalNavIcon
      );

    if (Array.isArray(children)) {
      hasGroup = children.some(child => isGroup(child));
      hasIcons = children.some(child => isIcon(child));
    } else {
      hasGroup = isGroup(children);
      hasIcons = isIcon(children);
    }

    const classNames = cn('clr-vertical-nav', {
      'is-collapsed': collapsible && collapsed,
      'has-nav-groups': hasGroup,
      'has-icons': hasIcons
    });

    const toggleNav = () => {
      if (onStateChange) {
        onStateChange(!collapsed);
      }
    };

    const expandNav = () => {
      if (onStateChange) {
        onStateChange(true);
      }
    };

    const renderCollapsibleButton = () => {
      return collapsible ? (
        <button
          className="nav-trigger"
          aria-label="Toggle vertical navigation"
          onClick={toggleNav}
          aria-expanded={!collapsed}>
          <clr-icon
            shape="angle-double"
            class="nav-trigger-icon"
            dir={collapsed ? 'right' : 'left'}
            title={collapsed ? 'Expand' : 'Collapse'}
          />
        </button>
      ) : null;
    };

    const renderNavGroup = () => {
      return React.Children.map(children, child => {
        if (
          React.isValidElement<ChildProps>(child) &&
          child.type === VerticalNavGroup
        ) {
          return React.cloneElement(child, {
            expanded: !collapsed,
            onExpand: expandNav
          });
        } else {
          return child;
        }
      });
    };

    const renderHiddenExpandButton = () => {
      return collapsible && collapsed ? (
        <button
          onClick={expandNav}
          aria-label="Expand vertical navigation"
          className="nav-btn"
        />
      ) : null;
    };

    return (
      <div
        ref={ref}
        className={classNames}
        role="navigation"
        aria-label="Vertical Navigation">
        {renderCollapsibleButton()}
        <div className="nav-content" role="list">
          {renderNavGroup()}
          {renderHiddenExpandButton()}
        </div>
      </div>
    );
  }
);

interface GroupProps {
  children?: React.ReactNode;
  expanded?: boolean;
  path: string;
  onExpand?: () => void;
}

export const VerticalNavGroup = React.forwardRef<HTMLDivElement, GroupProps>(
  (props, ref) => {
    const { children, onExpand, path } = props;
    const [expanded, setExpanded] = useState(props.expanded || false);

    useEffect(() => {
      if (!props.expanded) {
        setExpanded(false);
      }
    }, [props.expanded]);

    const toggleGroup = () => {
      onExpand!();
      setExpanded(prevExpanded => !prevExpanded);
    };

    const renderNavIcon = () => {
      return React.Children.map(children, child => {
        if (
          React.isValidElement<IconProps>(child) &&
          child.type === VerticalNavIcon
        ) {
          return child;
        } else {
          return null;
        }
      });
    };

    const renderNavText = () => {
      return React.Children.map(children, child => {
        if (!React.isValidElement(child)) {
          return child;
        } else {
          return null;
        }
      });
    };

    const renderNavGroupChildren = () => {
      if (expanded) {
        return React.Children.map(children, child => {
          if (
            React.isValidElement(child) &&
            child.type === VerticalNavGroupChildren
          ) {
            return child;
          } else {
            return null;
          }
        });
      } else {
        return null;
      }
    };

    const renderNavGroupContent: React.FunctionComponent<
      RouteComponentProps
    > = props => {
      const { match } = props;

      return (
        <div
          className={cn('nav-group-content', { active: match && !expanded })}>
          <button className="nav-group-trigger" onClick={toggleGroup}>
            {renderNavIcon()}
            <div className="nav-group-text">{renderNavText()}</div>
            <clr-icon
              class="nav-group-trigger-icon"
              shape="caret"
              dir={expanded ? 'down' : 'right'}
            />
          </button>
        </div>
      );
    };

    return (
      <div ref={ref} className={cn('nav-group', { 'is-expanded': expanded })}>
        <Route path={path} component={renderNavGroupContent} />
        <div className="nav-group-children">{renderNavGroupChildren()}</div>
      </div>
    );
  }
);

interface IconProps {
  shape: string;
  active?: boolean;
}

export const VerticalNavIcon: React.FC<IconProps> = props => {
  const { shape, active } = props;
  return (
    <clr-icon class={cn('nav-icon', { 'is-solid': active })} shape={shape} />
  );
};

interface LinkProps {
  title?: string;
  children?: React.ReactNode;
  routeTo: string;
  exact?: boolean;
}

export const VerticalNavLink = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (props, ref) => {
    const { children, routeTo, title } = props;

    const renderNavIcon = () => {
      return React.Children.map(children, child => {
        if (
          React.isValidElement<IconProps>(child) &&
          child.type === VerticalNavIcon
        ) {
          return child;
        } else {
          return null;
        }
      });
    };

    const renderNavText = () => {
      return React.Children.map(children, child => {
        if (!React.isValidElement(child)) {
          return child;
        } else {
          return null;
        }
      });
    };

    return (
      <div role="listitem">
        <NavLink
          ref={ref}
          className="nav-link"
          to={routeTo}
          activeClassName="active"
          title={title}>
          {renderNavIcon()}
          <span className="nav-text">{renderNavText()}</span>
        </NavLink>
      </div>
    );
  }
);

export const VerticalNavGroupChildren: React.FunctionComponent<
  React.PropsWithChildren<NavProps>
> = props => {
  return <>{props.children}</>;
};
